import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Avatar,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function ResultBadge({ value }) {
  const isWin = value === "W";

  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: ".7rem",
        fontWeight: 800,
        color: "#fff",
        backgroundColor: isWin ? "#0EB07A" : "#dc2626",
      }}
    >
      {value}
    </Box>
  );
}

function StandingsSection() {
  const [categories, setCategories] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const loadStandings = () => {
    fetch("/standings/list/")
      .then((res) => res.json())
      .then((data) => {
        setCategories(data || []);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadStandings();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadStandings();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const activeCategory = categories[tabValue];

  return (
    <Box component="section" sx={{ py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: { xs: "1.8rem", md: "2.3rem" },
            color: "primary.main",
            mb: 1,
          }}
        >
          Standings
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : !categories.length ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "18px",
              border: "1px solid #e5e7eb",
              py: 4,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              No standings available
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: "18px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map((cat) => (
                  <Tab key={cat.category_id} label={cat.category} />
                ))}
              </Tabs>
            </Paper>

            {activeCategory && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "20px",
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: "primary.main",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {activeCategory.category} Table
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        {[
                          "RANK",
                          "TEAM",
                          "GP",
                          "W",
                          "L",
                          "PF",
                          "PA",
                          "DIFF",
                          "PTS",
                          "FORM",
                        ].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              fontWeight: 800,
                              color: "primary.main",
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {activeCategory.table.map((row, index) => (
                        <TableRow
                          key={row.team_id}
                          sx={{
                            backgroundColor:
                              index === 0
                                ? "rgba(8,112,162,.05)"
                                : "#fff",
                          }}
                        >
                          <TableCell sx={{ fontWeight: 800 }}>
                            {row.rank || index + 1}
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Avatar
                                src={row.team_logo || ""}
                                alt={row.team}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: "#f3f4f6",
                                }}
                              />

                              <Typography sx={{ fontWeight: 700 }}>
                                {row.team}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>{row.gp}</TableCell>
                          <TableCell>{row.w}</TableCell>
                          <TableCell>{row.l}</TableCell>
                          <TableCell>{row.pf}</TableCell>
                          <TableCell>{row.pa}</TableCell>

                          <TableCell sx={{ fontWeight: 700 }}>
                            {row.diff > 0
                              ? `+${row.diff}`
                              : row.diff}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight: 800,
                              color: "primary.main",
                            }}
                          >
                            {row.pts}
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {row.form?.length ? (
                                row.form.map((r, i) => (
                                  <ResultBadge key={i} value={r} />
                                ))
                              ) : (
                                "-"
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            variant="contained"
            onClick={() => window.open("/basketball-rules", "_blank")}
            sx={{
              borderRadius: "12px",
              fontWeight: 700,
              px: 3,
              py: 1,
              mt: 4,
            }}
          >
            Basketball Rules
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default StandingsSection;